// Who is playing a session, as a name (spec/13 §5: "who is playing, on which station").
//
// A session carries only the player's id. With `players:read` the name comes from the same profile
// read the Players screen uses (one cached read per player, shared by the list, the sheet and the
// Players drawer); without it — or while the read is in flight, or if it fails — the short id stands in,
// with the whole id on hover and for a screen reader. The server checks `players:read` again.

import { useTranslation } from '@clavian/i18n/react';
import type { ReactNode } from 'react';

import { playerName, usePlayerProfile } from '../players/queries';

export interface SessionPlayerProps {
  readonly organizationId: string;
  readonly playerId: string;
  /** Whether this staff member may read players (`players:read`). */
  readonly canReadPlayers: boolean;
}

export function SessionPlayer({ organizationId, playerId, canReadPlayers }: SessionPlayerProps): ReactNode {
  const { t } = useTranslation('console');
  const profile = usePlayerProfile(organizationId, canReadPlayers ? playerId : undefined);
  if (profile.data === undefined) {
    return (
      <span className="console-code" title={playerId} aria-label={playerId}>
        {t('sessions.field.playerShort', { id: playerId.slice(-6).toUpperCase() })}
      </span>
    );
  }
  const name = playerName(profile.data);
  const handle = profile.data.username !== null && profile.data.username !== name ? `@${profile.data.username}` : null;
  return (
    <span className="console-session-player">
      <span className="console-session-player__name">{name}</span>
      {handle === null ? null : <span className="console-cell-sub">{handle}</span>}
    </span>
  );
}
